import React from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import {
  IGetUserOrdersQuery,
  IGetUserOrdersQueryVariables,
  useGetUserOrdersQuery,
} from 'generated/graphql-gateway';
import { RbiOrderStatus } from 'generated/rbi-graphql';
import { useAuthContext } from 'state/auth';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

export const UserOrdersDefaultLimit = 5;

export const UserOrdersDefaultVariables = {
  orderStatuses: [
    RbiOrderStatus.INSERT_SUCCESSFUL,
    RbiOrderStatus.UPDATE_SUCCESSFUL,
    RbiOrderStatus.REFUND_SUCCESSFUL,
    RbiOrderStatus.REFUND_ERROR,
    RbiOrderStatus.REFUND_REQUESTED,
  ],
  shouldFilterByRegion: true,
};

export const useUserOrders = (
  variables: IGetUserOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const [ordersData, setOrdersData] = React.useState<IGetUserOrdersQuery>();
  const [fetchingMore, setFetchingMore] = React.useState<boolean>(false);
  const { isAuthenticated } = useAuthContext();
  const { logEvent } = useMParticleContext();
  const { error, loading, refetch, fetchMore } = useGetUserOrdersQuery({
    variables: {
      ...UserOrdersDefaultVariables,
      ...variables,
    },
    onCompleted: data => {
      setOrdersData(data);
    },
    context: { shouldNotBatch: true },
    skip: !isAuthenticated,
    fetchPolicy,
  });

  const resumeAfter = ordersData?.userOrders.resumeAfter;
  const count = ordersData?.userOrders.count || 0;
  const orders = ((ordersData?.userOrders.orders || []) as unknown) as IHistoricalOrder[];
  const handleLoadMore = async () => {
    if (!resumeAfter) {
      return;
    }

    logEvent(CustomEventNames.LOAD_MORE, EventTypes.Other, {
      'Source Page': '/recentitems',
    });

    setFetchingMore(true);
    try {
      await fetchMore({
        variables: { resumeAfter },
        updateQuery: (prev, { fetchMoreResult }) => {
          const prevOrders = ordersData?.userOrders.orders || [];
          const currentOrders = fetchMoreResult.userOrders.orders || [];
          const userOrders = {
            ...fetchMoreResult,
            userOrders: {
              ...fetchMoreResult.userOrders,
              orders: [...prevOrders, ...currentOrders],
            },
          };
          setOrdersData(userOrders);
          return userOrders;
        },
      });
    } catch {
      setFetchingMore(false);
    } finally {
      setFetchingMore(false);
    }
  };

  return {
    error,
    loading,
    orders,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
  };
};
